import React, { useState, useEffect, useRef, useCallback } from "react";

// ─── API Configuration ────────────────────────────────────────────────────────
// Empty string — Nginx on VPS 1 proxies /api/ to the Node.js server
// For local development change to: "http://localhost:3001"
const API_URL = "https://www.teevra.ai/diagnostic";
const API_SECRET = "0ec7950b52d07fcd3db28c5e22a2f15697132064bd8c6dcc9ef1d806008bff73";

// ─── Session start — called after data capture, before test begins ────────────
async function apiStartSession(data) {
  try {
    const res = await fetch(`${API_URL}/api/sessions/start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_SECRET,
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
    const { sessionId } = await res.json();
    return sessionId;
  } catch (e) {
    console.warn("Session start failed, continuing offline:", e.message);
    return "offline_" + Date.now();
  }
}

// ─── Session complete — called after last trial, saves scores + all trials ────
async function apiCompleteSession(sessionId, scores, trials) {
  try {
    const res = await fetch(`${API_URL}/api/sessions/complete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_SECRET,
      },
      body: JSON.stringify({ sessionId, scores, trials }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  } catch (e) {
    console.warn("Session complete failed, buffering to localStorage:", e.message);
    // Offline fallback — data is preserved locally and can be re-submitted
    localStorage.setItem(
      `teevra_fallback_${sessionId}`,
      JSON.stringify({ scores, trials, failedAt: new Date().toISOString() })
    );
  }
}

// ─── Trial Generation ───────────────────────────────────────────────────────
const TARGET_SHAPES = ["triangle", "square", "pentagon", "star", "diamond"];
const NON_TARGET = "circle";
const ISIS = [1000, 2000, 4000];
const BLOCKS = 6;
const STIMULUS_DURATION = 450; // ms — shape visible on screen

// Jitter: ±15% of base ISI, uniform random, rounded to nearest ms
function applyJitter(isi) {
  const window = isi * 0.15;
  return Math.round(isi + (Math.random() * 2 - 1) * window);
}

function generateTrials() {
  const trials = [];
  for (let b = 0; b < BLOCKS; b++) {
    // Each block: 20 trials per ISI, shuffled
    const blockTrials = [];
    for (let isiIdx = 0; isiIdx < 3; isiIdx++) {
      const isi = ISIS[isiIdx];
      for (let t = 0; t < 20; t++) {
        const isTarget = Math.random() < 0.80;
        const shape = isTarget
          ? TARGET_SHAPES[Math.floor(Math.random() * TARGET_SHAPES.length)]
          : NON_TARGET;
        // actualIsi is what the test uses; isi is the nominal condition stored for analysis
        blockTrials.push({ block: b + 1, isi, actualIsi: applyJitter(isi), shape, isTarget });
      }
    }
    // Shuffle block
    for (let i = blockTrials.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [blockTrials[i], blockTrials[j]] = [blockTrials[j], blockTrials[i]];
    }
    trials.push(...blockTrials);
  }
  return trials;
}

function generatePracticeTrials() {
  const trials = [];
  for (let i = 0; i < 7; i++) {
    const isTarget = i !== 2 && i !== 5; // circles at positions 3 and 6
    const shape = isTarget
      ? TARGET_SHAPES[Math.floor(Math.random() * TARGET_SHAPES.length)]
      : NON_TARGET;
    trials.push({ block: 0, isi: 3000, actualIsi: 3000, shape, isTarget });
  }
  return trials;
}

// ─── Score Calculation ──────────────────────────────────────────────────────
function computeScores(results) {
  const targets = results.filter((r) => r.isTarget);
  const nonTargets = results.filter((r) => !r.isTarget);
  const hits = targets.filter((r) => r.responded);
  const misses = targets.filter((r) => !r.responded);
  const commissions = nonTargets.filter((r) => r.responded);
  const hitRTs = hits.map((r) => r.rt).filter((rt) => rt > 100);
  const meanRT = hitRTs.length ? Math.round(hitRTs.reduce((a, b) => a + b, 0) / hitRTs.length) : 0;
  const variance = hitRTs.length
    ? hitRTs.reduce((a, b) => a + Math.pow(b - meanRT, 2), 0) / hitRTs.length
    : 0;
  const rtStdDev = Math.round(Math.sqrt(variance));
  const perseverations = results.filter((r) => r.rt > 0 && r.rt < 100).length;
  const omissionPct = targets.length ? parseFloat(((misses.length / targets.length) * 100).toFixed(1)) : 0;
  const commissionPct = nonTargets.length ? parseFloat(((commissions.length / nonTargets.length) * 100).toFixed(1)) : 0;

  // Vigilance: compare accuracy in blocks 1-2 vs 5-6
  const earlyAcc = results.filter((r) => r.block <= 2 && r.isTarget && r.responded).length /
    Math.max(1, results.filter((r) => r.block <= 2 && r.isTarget).length);
  const lateAcc = results.filter((r) => r.block >= 5 && r.isTarget && r.responded).length /
    Math.max(1, results.filter((r) => r.block >= 5 && r.isTarget).length);
  const vigilanceDecrement = parseFloat((earlyAcc - lateAcc).toFixed(3));

  // Block-by-block accuracy and RT
  const blockStats = Array.from({ length: 6 }, (_, i) => {
    const bTrials = results.filter((r) => r.block === i + 1);
    const bTargets = bTrials.filter((r) => r.isTarget);
    const bHits = bTargets.filter((r) => r.responded);
    const bRTs = bHits.map((r) => r.rt).filter((rt) => rt > 100);
    return {
      block: i + 1,
      accuracy: bTargets.length ? Math.round((bHits.length / bTargets.length) * 100) : 0,
      meanRT: bRTs.length ? Math.round(bRTs.reduce((a, b) => a + b, 0) / bRTs.length) : 0,
    };
  });

  // ── Validity Gate ─────────────────────────────────────────────────────────────
  // All three criteria must pass for TAS to be computed and reported.
  // Raw metrics are always returned so the database still receives them.

  const totalResponses = results.filter((r) => r.responded).length;
  const responseRate = parseFloat(((totalResponses / Math.max(results.length, 360)) * 100).toFixed(1));
  const validHitCount = hitRTs.length;
  const hasEmptyBlock = blockStats.some((b, i) => {
    const blockResponses = results.filter(r => r.block === i + 1 && r.responded).length;
    return blockResponses === 0;
  });

  const validationFailures = [];
  if (responseRate < 20)
    validationFailures.push(`Response rate was ${responseRate}% — minimum 20% required`);
  if (validHitCount < 30)
    validationFailures.push(`Only ${validHitCount} valid responses recorded — minimum 30 required for reliable scoring`);
  if (hasEmptyBlock)
    validationFailures.push("One or more blocks had zero responses — sustained attention score is unreliable");

  const isValidSession = validationFailures.length === 0;

  // If session is invalid, return null TAS — raw metrics still stored to database
  if (!isValidSession) {
    return {
      tas: null, percentile: null, isValidSession, validationFailures, responseRate,
      tasDetection: null, tasInhibition: null, tasConsistency: null, tasVigilance: null,
      omissionPct, commissionPct, hitRate: parseFloat(((hits.length / Math.max(1, targets.length)) * 100).toFixed(1)),
      meanRT, rtStdDev, perseverations, vigilanceDecrement, vigilanceSlope: 0,
      isAbandoned: false, vigilanceSlopeConfidence: 0, blockStats,
    };
  }

  // ── Vigilance slope — linear regression of accuracy across 6 blocks ─────────
  const blockAccuracies = blockStats.map((b) => b.accuracy);
  const n = blockAccuracies.length;
  const xMean = (n - 1) / 2;
  const yMean = blockAccuracies.reduce((a, b) => a + b, 0) / n;
  const slopeNum = blockAccuracies.reduce((s, y, x) => s + (x - xMean) * (y - yMean), 0);
  const slopeDen = blockAccuracies.reduce((s, _, x) => s + Math.pow(x - xMean, 2), 0);
  const vigilanceSlope = slopeDen ? parseFloat((slopeNum / slopeDen).toFixed(3)) : 0;

  // Sub-fix A — slope confidence: only count blocks with at least 5 valid hits
  // Blocks with too few responses produce unreliable accuracy estimates that
  // distort the regression. Confidence scales from 0 to 1 as valid blocks approach 6.
  const MIN_HITS_PER_BLOCK = 5;
  const validBlocks = blockStats.filter((b) => {
    const bHits = results.filter((r) => r.block === b.block && r.isTarget && r.responded && r.rt > 100);
    return bHits.length >= MIN_HITS_PER_BLOCK;
  }).length;
  const slopeConfidence = validBlocks / 6;

  // Sub-fix B — sigmoid mapping replaces the linear clamp
  // Linear clamp (old): slope of +2 and +10 both map to 25 — loses information
  // Sigmoid (new): handles extreme values gracefully without a hard boundary
  // At slope =  0: score ≈ 14.3 (flat — slight penalty, flat is not perfect)
  // At slope = +1: score ≈ 17.7 (mild improvement — warmup effect)
  // At slope = -2: score ≈  7.6 (declining — attentional fatigue)
  // At slope = -5: score ≈  2.2 (steep decline)
  const sigmoidScore = 25 / (1 + Math.exp(-0.8 * (vigilanceSlope + 1)));
  const tasVigilance = parseFloat((sigmoidScore * slopeConfidence).toFixed(2));

  // ── Abandonment detection — before component scoring so it informs interpretation
  // Distinguishes "gradual fatigue" from "stopped responding mid-test"
  // Three or more consecutive blocks with near-zero accuracy (< 10%) = abandoned
  let maxConsecutiveEmpty = 0;
  let currentRun = 0;
  for (const b of blockStats) {
    if (b.accuracy < 10) {
      currentRun++;
      maxConsecutiveEmpty = Math.max(maxConsecutiveEmpty, currentRun);
    } else {
      currentRun = 0;
    }
  }
  const isAbandoned = maxConsecutiveEmpty >= 3;

  // ── TAS Components ────────────────────────────────────────────────────────────
  // Component 1: Detection accuracy — hit rate as proportion of max
  const hitRate = targets.length ? (hits.length / targets.length) * 100 : 0;
  const tasDetection = parseFloat(((hitRate / 100) * 25).toFixed(2));

  // Component 2: Inhibitory control — inverse commission rate scaled by engagement
  // A person who never presses anything scores zero false alarms — but this reflects
  // absence, not control. We scale the inhibition credit by how much the person was
  // actually engaging with targets. Full credit only if hitting ≥ 80% of targets.
  const engagementRate = hits.length / Math.max(1, targets.length);
  const inhibitionBase = ((100 - commissionPct) / 100) * 25;
  const tasInhibition = parseFloat((inhibitionBase * Math.min(1, engagementRate / 0.8)).toFixed(2));

  // Component 3: Response consistency — CV-based (SD / mean RT)
  // CV of 0 = perfect (25pts), CV of 1.0+ = 0pts
  // Scaled by sample confidence — fewer than 30 valid hits produces an unreliable
  // standard deviation. Confidence grows linearly from 0 to 1 as hits approach 30.
  const MIN_RT_SAMPLE = 30;
  const cv = meanRT > 0 ? rtStdDev / meanRT : 1;
  const sampleConfidence = Math.min(1, validHitCount / MIN_RT_SAMPLE);
  const tasConsistency = parseFloat((Math.max(0, (1 - cv)) * 25 * sampleConfidence).toFixed(2));

  // Component 4: Sustained attention — computed above via sigmoid + slope confidence

  // TAS: sum of four components (0–100)
  const tas = Math.round(tasDetection + tasInhibition + tasConsistency + tasVigilance);

  // Percentile stored internally only — never shown in UI
  const percentile = Math.min(99, Math.max(1, Math.round(tas * 0.9 + Math.random() * 5)));

  return {
    tas, percentile, isValidSession: true, validationFailures: [], responseRate,
    tasDetection, tasInhibition, tasConsistency, tasVigilance,
    omissionPct, commissionPct, hitRate: parseFloat(hitRate.toFixed(1)),
    meanRT, rtStdDev, perseverations,
    vigilanceDecrement, vigilanceSlope, vigilanceSlopeConfidence: slopeConfidence,
    isAbandoned, blockStats,
  };
}

// ─── SVG Shapes ─────────────────────────────────────────────────────────────
function ShapeSVG({ shape, size = 125, color = "#5BAADB", filled = true, strokeWidth = 2.5 }) {
  const s = size;
  const h = s / 2;
  const m = 8;
  const props = filled
    ? { fill: color, stroke: "none" }
    : { fill: "none", stroke: color, strokeWidth, strokeLinejoin: "round", strokeLinecap: "round" };
  if (shape === "triangle") return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}><polygon points={`${h},${m} ${s - m},${s - m} ${m},${s - m}`} {...props} /></svg>;
  if (shape === "square") return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}><rect x={m} y={m} width={s - m * 2} height={s - m * 2} {...props} /></svg>;
  if (shape === "pentagon") {
    const cx = h, cy = h, r = h - m;
    const pts = Array.from({ length: 5 }, (_, i) => {
      const a = (i * 72 - 90) * (Math.PI / 180);
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    }).join(" ");
    return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}><polygon points={pts} {...props} /></svg>;
  }
  if (shape === "star") {
    const cx = h, cy = h, ro = h - m, ri = ro * 0.42;
    const pts = Array.from({ length: 10 }, (_, i) => {
      const a = (i * 36 - 90) * (Math.PI / 180);
      const r = i % 2 === 0 ? ro : ri;
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    }).join(" ");
    return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}><polygon points={pts} {...props} /></svg>;
  }
  if (shape === "diamond") return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}><polygon points={`${h},${m} ${s - m},${h} ${h},${s - m} ${m},${h}`} {...props} /></svg>;
  if (shape === "circle") return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}><circle cx={h} cy={h} r={h - m} {...props} /></svg>;
  return null;
}

// ─── Design tokens ───────────────────────────────────────────────────────────
const C = {
  navy: "#0D1C2E",
  navyDeep: "#060E18",
  blue: "#2380BF",
  blueLight: "#5BAADB",
  cream: "#F1EFE8",
  sand: "#FAFAF8",
  muted: "#888780",
  border: "#D3D1C7",
  borderLight: "#E8E6DF",
  text: "#1A1A17",
  textDark: "#2C2C2A",
};

const S = {
  sidebar: { width: 206, bg: C.navy },
  font: { serif: "'DM Serif Display', serif", sans: "'DM Sans', sans-serif" },
};

// ─── Shared layout components ────────────────────────────────────────────────
function Sidebar({ currentStep }) {
  const steps = [
    "Introduction", "Consent & data", "Participant details",
    "Instructions", "Practice round", "Main test", "Report",
  ];
  return (
    <div style={{ width: S.sidebar.width, minWidth: S.sidebar.width, background: S.sidebar.bg, padding: "32px 24px", display: "flex", flexDirection: "column" }}>
      <div style={{ fontFamily: S.font.serif, fontSize: 17, color: C.cream, marginBottom: 4 }}>
        Teevra <em style={{ fontStyle: "italic", color: C.blueLight }}>Diagnostic</em>
      </div>
      <div style={{ fontFamily: S.font.sans, fontSize: 10, color: "rgba(255,255,255,0.25)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 36 }}>Attention Assessment</div>
      {steps.map((label, i) => {
        const n = i + 1;
        const done = n < currentStep;
        const active = n === currentStep;
        return (
          <div key={n} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "9px 0", position: "relative" }}>
            {i < steps.length - 1 && <div style={{ position: "absolute", left: 10, top: 32, bottom: -9, width: 1, background: "rgba(255,255,255,0.07)" }} />}
            <div style={{ width: 20, height: 20, minWidth: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: S.font.sans, fontSize: 10, fontWeight: 500, marginTop: 1, background: done ? C.blue : active ? C.cream : "rgba(255,255,255,0.08)", color: done ? "#fff" : active ? C.navy : "rgba(255,255,255,0.3)" }}>
              {done ? "✓" : n}
            </div>
            <div style={{ fontFamily: S.font.sans, fontSize: 12, lineHeight: 1.3, color: active ? C.cream : done ? "rgba(255,255,255,0.48)" : "rgba(255,255,255,0.28)", fontWeight: active ? 500 : 400 }}>{label}</div>
          </div>
        );
      })}
    </div>
  );
}

function PageShell({ step, children }) {
  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: S.font.sans, background: C.sand }}>
      <Sidebar currentStep={step} />
      <div style={{ flex: 1, padding: "40px 44px", overflowY: "auto", maxWidth: 800 }}>
        {children}
      </div>
    </div>
  );
}

function Eyebrow({ n, total }) {
  return <div style={{ fontFamily: S.font.sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", color: C.blue, marginBottom: 8 }}>Step {n} of {total}</div>;
}

function Heading({ children }) {
  return <div style={{ fontFamily: S.font.serif, fontSize: 30, color: C.text, marginBottom: 6, lineHeight: 1.15 }}>{children}</div>;
}

function Subhead({ children }) {
  return <div style={{ fontFamily: S.font.sans, fontSize: 13, color: C.muted, marginBottom: 24, lineHeight: 1.6 }}>{children}</div>;
}

function Field({ label, children, span = 1 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, gridColumn: `span ${span}` }}>
      <div style={{ fontFamily: S.font.sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted }}>{label}</div>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text", readOnly, style }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} readOnly={readOnly}
      style={{ background: readOnly ? C.cream : "white", border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontFamily: S.font.sans, fontSize: 13, color: readOnly ? C.muted : C.textDark, outline: "none", width: "100%", ...style }} />
  );
}

function Select({ value, onChange, children, style }) {
  return (
    <select value={value} onChange={onChange}
      style={{ background: "white", border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontFamily: S.font.sans, fontSize: 13, color: C.textDark, outline: "none", width: "100%", appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888780' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", ...style }}>
      {children}
    </select>
  );
}

function BtnPrimary({ onClick, children, disabled, style }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ background: disabled ? C.muted : C.blue, color: "#fff", border: "none", borderRadius: 100, padding: "13px 36px", fontFamily: S.font.sans, fontSize: 14, fontWeight: 500, cursor: disabled ? "not-allowed" : "pointer", transition: "opacity 0.15s", ...style }}>
      {children}
    </button>
  );
}

function BtnBack({ onClick }) {
  return <button onClick={onClick} style={{ background: "none", border: "none", fontFamily: S.font.sans, fontSize: 13, color: C.muted, cursor: "pointer" }}>← Back</button>;
}

function Checkbox({ checked, onChange, label }) {
  return (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", fontFamily: S.font.sans, fontSize: 13, color: "#5F5E5A", lineHeight: 1.6, marginBottom: 10 }}>
      <div onClick={onChange} style={{ width: 16, height: 16, minWidth: 16, border: `1.5px solid ${checked ? C.blue : "#B4B2A9"}`, borderRadius: 4, marginTop: 2, display: "flex", alignItems: "center", justifyContent: "center", background: checked ? C.blue : "white", cursor: "pointer", flexShrink: 0 }}>
        {checked && <div style={{ width: 8, height: 5, borderLeft: "1.5px solid white", borderBottom: "1.5px solid white", transform: "rotate(-45deg) translateY(-1px)" }} />}
      </div>
      <span>{label}</span>
    </label>
  );
}

function InfoBox({ children, style }) {
  return (
    <div style={{ background: "#EBF4FB", border: `0.5px solid #7ABFE0`, borderRadius: 10, padding: "13px 16px", marginBottom: 18, display: "flex", gap: 10, alignItems: "flex-start", fontFamily: S.font.sans, fontSize: 12, color: "#1a4a7a", lineHeight: 1.65, ...style }}>
      <span style={{ fontSize: 15, marginTop: 1 }}>ℹ</span>
      <span>{children}</span>
    </div>
  );
}

function SectionLabel({ children }) {
  return <div style={{ fontFamily: S.font.sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.09em", textTransform: "uppercase", color: "#B4B2A9", marginBottom: 12, paddingBottom: 8, borderBottom: `0.5px solid ${C.borderLight}` }}>{children}</div>;
}

function Divider() {
  return <hr style={{ border: "none", borderTop: `0.5px solid ${C.borderLight}`, margin: "20px 0" }} />;
}

// ─── Screen 1: Intro ─────────────────────────────────────────────────────────
function ScreenIntro({ onNext }) {
  return (
    <div style={{ minHeight: "100vh", background: C.navyDeep, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 40px", position: "relative", overflow: "hidden" }}>
      {/* Radial glow */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "radial-gradient(ellipse 60% 45% at 50% 15%, #1a4a7a 0%, transparent 70%)", pointerEvents: "none" }} />
      {/* Ghost shapes */}
      <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }} viewBox="0 0 900 600">
        <polygon points="60,80 100,20 140,80" fill="none" stroke="#2380BF" strokeWidth="1" opacity="0.12" />
        <rect x="780" y="55" width="70" height="70" fill="none" stroke="#2380BF" strokeWidth="1" opacity="0.1" />
        <polygon points="28,440 60,380 92,440 78,488 42,488" fill="none" stroke="#2380BF" strokeWidth="1" opacity="0.1" />
        <circle cx="830" cy="460" r="40" fill="none" stroke="#2380BF" strokeWidth="1" opacity="0.08" />
        <polygon points="700,520 722,488 744,520" fill="none" stroke="#2380BF" strokeWidth="1" opacity="0.07" />
        <rect x="130" y="200" width="36" height="36" fill="none" stroke="#2380BF" strokeWidth="1" opacity="0.07" />
      </svg>

      <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ background: "rgba(35,128,191,0.15)", border: "0.5px solid rgba(35,128,191,0.35)", borderRadius: 100, padding: "5px 18px", fontFamily: S.font.sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(35,128,191,0.9)", marginBottom: 28 }}>
          Attention Screening · Non-clinical
        </div>
        <div style={{ fontFamily: S.font.serif, fontSize: 58, color: C.cream, lineHeight: 1, textAlign: "center", marginBottom: 6 }}>
          Teevra <em style={{ fontStyle: "italic", color: C.blueLight }}>Diagnostic</em>
        </div>
        <div style={{ fontFamily: S.font.sans, fontSize: 12, fontWeight: 300, color: "rgba(255,255,255,0.3)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 8 }}>
          Computerised attention assessment
        </div>
        <div style={{ fontFamily: S.font.sans, fontSize: 12, color: "rgba(35,128,191,0.55)", letterSpacing: "0.05em", marginBottom: 40 }}>
          तीव्र — meaning "sharp" or "intense" in Sanskrit
        </div>
        <div style={{ fontFamily: S.font.sans, fontSize: 15, fontWeight: 300, color: "rgba(255,255,255,0.55)", textAlign: "center", maxWidth: 400, lineHeight: 1.8, marginBottom: 44 }}>
          A standardised computerised task to assess attention, impulsivity, and response consistency in children and adolescents.
        </div>
        <button onClick={onNext} style={{ background: C.blue, color: "#fff", border: "none", borderRadius: 100, padding: "15px 44px", fontFamily: S.font.sans, fontSize: 15, fontWeight: 500, cursor: "pointer", marginBottom: 24 }}>
          Begin assessment →
        </button>
        <div style={{ fontFamily: S.font.sans, fontSize: 11, color: "rgba(255,255,255,0.2)", textAlign: "center", maxWidth: 360, lineHeight: 1.7 }}>
          Not a clinical diagnosis. Results should be reviewed by a qualified professional. Duration: approx. 16 minutes. Data stored securely on Teevra servers.
        </div>
      </div>
    </div>
  );
}

// ─── Screen 2: Consent ───────────────────────────────────────────────────────
function ScreenConsent({ onNext, onBack, onConsent }) {
  const [checks, setChecks] = useState({ c1: false, c2: false, c3: false, c4: false, d1: false, d2: false, d3: false, d4: false });
  const [guardian, setGuardian] = useState({ name: "", relationship: "" });
  const toggle = (k) => setChecks((p) => ({ ...p, [k]: !p[k] }));
  const allChecked = Object.values(checks).every(Boolean) && guardian.name.trim() && guardian.relationship;

  const handleAgree = () => {
    onConsent({ guardianName: guardian.name.trim(), guardianRelation: guardian.relationship });
    onNext();
  };

  return (
    <PageShell step={2}>
      <Eyebrow n={2} total={7} />
      <Heading>Consent &amp; data permissions</Heading>
      <Subhead>A parent or guardian must provide consent for participants under 16. Please read the data storage terms carefully before proceeding.</Subhead>

      <InfoBox>
        <span><strong style={{ fontWeight: 500 }}>Your data is stored securely and encrypted.</strong> Assessment responses, reaction times, and participant details are used solely for reporting and benchmarking purposes. Your data is never sold or shared with any third party. You may request deletion at any time by contacting your administrator.</span>
      </InfoBox>

      {/* Assessment consent */}
      <div style={{ background: "white", border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", marginBottom: 16 }}>
        <div style={{ fontFamily: S.font.sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted, marginBottom: 14 }}>Assessment consent</div>
        <Checkbox checked={checks.c1} onChange={() => toggle("c1")} label="I understand this is a non-clinical screening tool and results do not constitute a medical or psychological diagnosis." />
        <Checkbox checked={checks.c2} onChange={() => toggle("c2")} label="I have explained the nature of this test to the child and they have agreed to participate willingly." />
        <Checkbox checked={checks.c3} onChange={() => toggle("c3")} label="I consent to the child completing this assessment and for results to be viewed by the named administrator." />
        <Checkbox checked={checks.c4} onChange={() => toggle("c4")} label="The child may stop at any time without consequence." />
      </div>

      {/* Data storage consent */}
      <div style={{ background: "white", border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}>
        <div style={{ fontFamily: S.font.sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted, marginBottom: 14 }}>Data storage &amp; privacy consent</div>
        <Checkbox checked={checks.d1} onChange={() => toggle("d1")} label="I consent to the participant's assessment data (responses, timings, scores) being stored securely on Teevra Diagnostic's servers." />
        <Checkbox checked={checks.d2} onChange={() => toggle("d2")} label="I understand that anonymised, aggregated data may be used for population-level research and normative benchmarking." />
        <Checkbox checked={checks.d3} onChange={() => toggle("d3")} label="I consent to the participant's data being retained for up to 5 years for longitudinal tracking, or until I request deletion." />
        <Checkbox checked={checks.d4} onChange={() => toggle("d4")} label={<span>I have read and accept the <span style={{ color: C.blue, textDecoration: "underline", cursor: "pointer" }}>Privacy Policy</span> and <span style={{ color: C.blue, textDecoration: "underline", cursor: "pointer" }}>Terms of Use</span>.</span>} />

        <div style={{ borderTop: `0.5px solid ${C.borderLight}`, marginTop: 14, paddingTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Parent / Guardian full name">
            <Input value={guardian.name} onChange={(e) => setGuardian((p) => ({ ...p, name: e.target.value }))} placeholder="Enter full name" />
          </Field>
          <Field label="Relationship to child">
            <Select value={guardian.relationship} onChange={(e) => setGuardian((p) => ({ ...p, relationship: e.target.value }))}>
              <option value="">Select…</option>
              <option>Mother</option><option>Father</option><option>Guardian</option><option>Other</option>
            </Select>
          </Field>
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <BtnBack onClick={onBack} />
        <BtnPrimary onClick={handleAgree} disabled={!allChecked}>Agree &amp; continue →</BtnPrimary>
      </div>
    </PageShell>
  );
}

// ─── Screen 3: Data Capture ──────────────────────────────────────────────────
function ScreenDataCapture({ onNext, onBack, onData }) {
  const [form, setForm] = useState({
    firstName: "", lastName: "",
    dobDay: "", dobMonth: "", dobYear: "",
    sex: "", grade: "", city: "", email: "",
    school: "", board: "CBSE", screenTime: "1 – 2 hours", screenActivity: "Mixed",
  });

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Days in selected month/year (handles Feb + leap years)
  const daysInMonth = (() => {
    const m = parseInt(form.dobMonth);
    const y = parseInt(form.dobYear);
    if (!m) return 31;
    if (!y) return new Date(2000, m, 0).getDate();
    return new Date(y, m, 0).getDate();
  })();

  const age = (() => {
    const d = parseInt(form.dobDay);
    const m = parseInt(form.dobMonth);
    const y = parseInt(form.dobYear);
    if (!d || !m || !y) return "";
    const diff = Date.now() - new Date(y, m - 1, d).getTime();
    const a = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
    return isNaN(a) || a < 3 || a > 70 ? "" : `${a} yrs`;
  })();

  // Formatted dob string for storage: DD/MM/YYYY
  const dob = form.dobDay && form.dobMonth && form.dobYear
    ? `${form.dobDay.padStart(2, "0")}/${form.dobMonth.padStart(2, "0")}/${form.dobYear}`
    : "";

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canProceed = form.firstName && form.lastName && dob && age &&
    form.sex && form.grade && form.city && emailValid && form.school;

  const handleNext = () => {
    onData({ ...form, dob, age });
    onNext();
  };

  // Year options: covers ages 3–70 (current year minus 3 down to current year minus 70)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 68 }, (_, i) => currentYear - 3 - i);

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  const selectStyle = {
    background: "white", border: `0.5px solid ${C.border}`, borderRadius: 8,
    padding: "10px 12px", fontFamily: S.font.sans, fontSize: 13, color: C.textDark,
    outline: "none", width: "100%", appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888780' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
  };

  return (
    <PageShell step={3}>
      <Eyebrow n={3} total={7} />
      <Heading>Participant details</Heading>
      <Subhead>All required fields must be completed. This data is stored securely and linked to assessment results.</Subhead>

      <SectionLabel>Child information</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
        <Field label="First name"><Input value={form.firstName} onChange={set("firstName")} placeholder="First name" /></Field>
        <Field label="Last name"><Input value={form.lastName} onChange={set("lastName")} placeholder="Last name" /></Field>
      </div>

      {/* DOB — three dropdowns */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontFamily: S.font.sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted, marginBottom: 5 }}>
          Date of birth
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1.4fr 1fr", gap: 8, alignItems: "end" }}>
          {/* Day */}
          <div>
            <div style={{ fontFamily: S.font.sans, fontSize: 10, color: "#B4B2A9", marginBottom: 4 }}>Day</div>
            <select value={form.dobDay} onChange={set("dobDay")} style={selectStyle}>
              <option value="">—</option>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          {/* Month */}
          <div>
            <div style={{ fontFamily: S.font.sans, fontSize: 10, color: "#B4B2A9", marginBottom: 4 }}>Month</div>
            <select value={form.dobMonth} onChange={set("dobMonth")} style={selectStyle}>
              <option value="">—</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          {/* Year */}
          <div>
            <div style={{ fontFamily: S.font.sans, fontSize: 10, color: "#B4B2A9", marginBottom: 4 }}>Year</div>
            <select value={form.dobYear} onChange={set("dobYear")} style={selectStyle}>
              <option value="">—</option>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          {/* Auto-calculated age */}
          <div>
            <div style={{ fontFamily: S.font.sans, fontSize: 10, color: "#B4B2A9", marginBottom: 4 }}>Age</div>
            <div style={{ background: C.cream, border: `0.5px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontFamily: S.font.sans, fontSize: 13, color: age ? "#1a4a7a" : C.muted, fontWeight: age ? 500 : 400, minHeight: 40, display: "flex", alignItems: "center" }}>
              {age || "—"}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 12 }}>
        <Field label="Sex">
          <Select value={form.sex} onChange={set("sex")}>
            <option value="">Select…</option><option>Male</option><option>Female</option><option>Prefer not to say</option>
          </Select>
        </Field>
        <Field label="Grade / Class"><Input value={form.grade} onChange={set("grade")} placeholder="e.g. Grade 4" /></Field>
        <Field label="City">
          <Select value={form.city} onChange={set("city")}>
            <option value="">Select…</option>
            <option>Bengaluru</option>
            <option>Delhi NCR</option>
            <option>Mumbai</option>
            <option>Hyderabad</option>
            <option>Chennai</option>
            <option>Pune</option>
            <option>Ahmedabad</option>
            <option>Others</option>
          </Select>
        </Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginBottom: 12 }}>
        <Field label="Email ID"><Input value={form.email} onChange={set("email")} placeholder="parent@email.com" type="email" /></Field>
      </div>

      <Divider />
      <SectionLabel>School information</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
        <Field label="School name"><Input value={form.school} onChange={set("school")} placeholder="School name" /></Field>
        <Field label="School board">
          <Select value={form.board} onChange={set("board")}>
            <option>CBSE</option><option>ICSE</option><option>IB (International Baccalaureate)</option><option>Cambridge (IGCSE)</option><option>State Board</option><option>Other</option>
          </Select>
        </Field>
      </div>

      <Divider />
      <SectionLabel>Screen time</SectionLabel>
      <InfoBox style={{ marginBottom: 14 }}>Daily non-educational screen time helps contextualise attention scores relative to digital habits.</InfoBox>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <Field label="Daily non-educational screen time">
          <Select value={form.screenTime} onChange={set("screenTime")}>
            <option>Less than 1 hour</option><option>1 – 2 hours</option><option>2 – 4 hours</option><option>4 – 6 hours</option><option>More than 6 hours</option>
          </Select>
        </Field>
        <Field label="Primary screen activity">
          <Select value={form.screenActivity} onChange={set("screenActivity")}>
            <option>Gaming</option><option>Streaming / videos</option><option>Social media</option><option>Mixed</option>
          </Select>
        </Field>
      </div>

      <div style={{ marginBottom: 24 }}></div>

      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <BtnBack onClick={onBack} />
        <BtnPrimary onClick={handleNext} disabled={!canProceed}>Continue to instructions →</BtnPrimary>
      </div>
    </PageShell>
  );
}

// ─── Screen 4: Instructions ──────────────────────────────────────────────────
const SHAPE_CARDS = [
  { shape: "triangle", label: "Triangle", press: true },
  { shape: "square", label: "Square", press: true },
  { shape: "pentagon", label: "Pentagon", press: true },
  { shape: "star", label: "Star", press: true },
  { shape: "diamond", label: "Diamond", press: true },
  { shape: "circle", label: "Circle", press: false },
];

function ScreenInstructions({ onNext, onBack }) {
  const [demoShape, setDemoShape] = useState("triangle");
  useEffect(() => {
    const shapes = ["triangle", "square", "star", "diamond", "pentagon"];
    let i = 0;
    const iv = setInterval(() => { i = (i + 1) % shapes.length; setDemoShape(shapes[i]); }, 1800);
    return () => clearInterval(iv);
  }, []);

  return (
    <PageShell step={4}>
      <Eyebrow n={4} total={7} />
      <Heading>How to play</Heading>
      <Subhead>Read these instructions carefully. A short practice round with feedback follows before the real test begins.</Subhead>

      {/* Demo panel */}
      <div style={{ background: C.navy, borderRadius: 14, padding: "24px 28px", display: "flex", alignItems: "center", gap: 28, marginBottom: 20 }}>
        <div style={{ width: 110, height: 96, minWidth: 110, background: C.navyDeep, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", border: `0.5px solid rgba(35,128,191,0.15)` }}>
          <div style={{ transition: "opacity 0.15s" }}><ShapeSVG shape={demoShape} size={68} color={C.blueLight} filled={false} /></div>
        </div>
        <div>
          <div style={{ fontFamily: S.font.serif, fontSize: 22, color: C.cream, lineHeight: 1.35, marginBottom: 8 }}>
            Press <em style={{ fontStyle: "italic", color: C.blueLight }}>Space</em> for every shape —{" "}
            <em style={{ fontStyle: "italic", color: C.blueLight }}>except</em> the circle
          </div>
          <div style={{ fontFamily: S.font.sans, fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>
            Shapes appear briefly on screen. Respond as quickly as you can. When you see a circle, do not press anything.
          </div>
        </div>
      </div>

      {/* Shape cards */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {SHAPE_CARDS.map(({ shape, label, press }) => (
          <div key={shape} style={{ flex: 1, background: press ? "white" : "#FCEBEB", border: `0.5px solid ${press ? C.border : "#F09595"}`, borderRadius: 10, padding: "12px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 7 }}>
            <ShapeSVG shape={shape} size={36} color={press ? "#3B6D11" : "#A32D2D"} filled={false} strokeWidth={2} />
            <div style={{ fontFamily: S.font.sans, fontSize: 11, fontWeight: 500, color: press ? "#5F5E5A" : "#A32D2D", textAlign: "center" }}>{label}</div>
            <div style={{ fontFamily: S.font.sans, fontSize: 10, fontWeight: 500, letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 100, background: press ? "#EAF3DE" : "#FCEBEB", color: press ? "#3B6D11" : "#A32D2D" }}>
              {press ? "Press ✓" : "Do not press ✕"}
            </div>
          </div>
        ))}
      </div>

      {/* Key hint */}
      <div style={{ background: "#EBF4FB", border: `0.5px solid #7ABFE0`, borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, marginBottom: 28, fontFamily: S.font.sans, fontSize: 13, color: "#1a4a7a", lineHeight: 1.5 }}>
        <div style={{ background: "white", border: "1px solid #7ABFE0", borderRadius: 6, padding: "4px 14px", fontSize: 12, fontWeight: 500, color: "#1a4a7a", boxShadow: "0 2px 0 #7ABFE0", whiteSpace: "nowrap" }}>Space bar</div>
        <span>or tap anywhere on screen (tablet/phone). Be as fast and accurate as possible. The test has no pause once started.</span>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <BtnBack onClick={onBack} />
        <BtnPrimary onClick={onNext}>Start practice round →</BtnPrimary>
      </div>
    </PageShell>
  );
}

// ─── Screen 5: Practice ──────────────────────────────────────────────────────
function ScreenPractice({ onNext, onBack }) {
  const trials = useRef(generatePracticeTrials());
  const [idx, setIdx] = useState(0);
  const [showing, setShowing] = useState(true);
  const [feedback, setFeedback] = useState(null); // "correct" | "wrong" | "miss"
  const respondedRef = useRef(false);
  const timerRef = useRef(null);
  const trialIsTargetRef = useRef(false);    // avoids reading trial.isTarget when trial is undefined
  const trialActualIsiRef = useRef(3000);    // avoids reading trial.actualIsi when trial is undefined

  const trial = trials.current[idx];
  const done = idx >= trials.current.length;

  // Keep refs in sync with current trial — only when trial exists
  if (trial) {
    trialIsTargetRef.current  = trial.isTarget;
    trialActualIsiRef.current = trial.actualIsi;
  }

  const handleResponse = useCallback(() => {
    if (respondedRef.current || done) return;
    respondedRef.current = true;
    setFeedback(trialIsTargetRef.current ? "correct" : "wrong");
  }, [done]);

  useEffect(() => {
    const handler = (e) => { if (e.code === "Space") { e.preventDefault(); handleResponse(); } };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleResponse]);

  useEffect(() => {
    if (done) return;
    let cancelled = false;

    // Read from refs — safe even if trial object is temporarily undefined
    const trialIsTarget  = trialIsTargetRef.current;
    const trialActualIsi = trialActualIsiRef.current;

    setShowing(true);
    setFeedback(null);
    respondedRef.current = false;

    timerRef.current = setTimeout(() => {
      if (cancelled) return;
      setShowing(false);

      const responseWindow = trialActualIsi - STIMULUS_DURATION;
      timerRef.current = setTimeout(() => {
        if (cancelled) return;
        if (!respondedRef.current && trialIsTarget) {
          respondedRef.current = true;
          setFeedback("miss");
        }
        timerRef.current = setTimeout(() => {
          if (cancelled) return;
          setFeedback(null);
          timerRef.current = setTimeout(() => {
            if (cancelled) return;
            setIdx((i) => i + 1);
          }, 400);
        }, 800);
      }, responseWindow);
    }, STIMULUS_DURATION);

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
  }, [idx, done]);

  if (done) {
    return (
      <div style={{ minHeight: "100vh", background: C.navyDeep, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 24 }}>
        <div style={{ fontFamily: S.font.serif, fontSize: 36, color: C.cream }}>Practice complete!</div>
        <div style={{ fontFamily: S.font.sans, fontSize: 15, color: "rgba(255,255,255,0.5)", marginBottom: 12 }}>The real test has 360 trials across 6 blocks. No feedback will be shown.</div>
        <BtnPrimary onClick={onNext}>Begin main test →</BtnPrimary>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.navyDeep, display: "flex", flexDirection: "column" }} onClick={handleResponse}>
      {/* Banner */}
      <div style={{ background: "#0F3D2E", padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        <span style={{ fontFamily: S.font.sans, fontSize: 12, fontWeight: 500, color: "#5DCAA5", letterSpacing: "0.04em" }}>● Practice mode — feedback shown after each response</span>
      </div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 28px", borderBottom: "0.5px solid rgba(255,255,255,0.05)" }}>
        <div style={{ fontFamily: S.font.serif, fontSize: 15, color: "rgba(255,255,255,0.25)" }}>Teevra <em style={{ fontStyle: "italic", color: "rgba(35,128,191,0.5)" }}>Diagnostic</em></div>
        <div style={{ fontFamily: S.font.sans, fontSize: 13, color: "rgba(255,255,255,0.35)" }}>Trial {idx + 1} of {trials.current.length}</div>
      </div>

      {/* Stage */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 36 }}>
        <div style={{ width: 220, height: 220, background: "#0D1C2E", borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", border: `0.5px solid ${feedback === "correct" ? "rgba(93,202,165,0.3)" : feedback === "wrong" ? "rgba(226,75,74,0.3)" : "rgba(35,128,191,0.12)"}` }}>
          {showing && trial && <ShapeSVG shape={trial.shape} color={feedback === "correct" ? "#5DCAA5" : feedback === "wrong" ? "#E24B4A" : C.blueLight} />}
        </div>

        {feedback === "correct" && <div style={{ fontFamily: S.font.serif, fontSize: 22, color: "#5DCAA5" }}>Correct! ✓</div>}
        {feedback === "wrong" && <div style={{ fontFamily: S.font.serif, fontSize: 22, color: "#E24B4A" }}>That was a circle — don't press! ✕</div>}
        {feedback === "miss" && <div style={{ fontFamily: S.font.serif, fontSize: 22, color: "#EF9F27" }}>Too slow — press faster!</div>}
        {!feedback && <div style={{ fontFamily: S.font.sans, fontSize: 13, color: "rgba(255,255,255,0.18)", textAlign: "center" }}>Press <strong style={{ color: "rgba(255,255,255,0.38)" }}>Space</strong> or <strong style={{ color: "rgba(255,255,255,0.38)" }}>tap</strong> for every shape — except the circle</div>}
      </div>
    </div>
  );
}

// ─── Countdown screen — shown before main test starts ────────────────────────
function ScreenCountdown({ onDone }) {
  const [count, setCount] = useState(3);
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  useEffect(() => {
    if (count === 0) {
      // Show "Go" for 400ms before transitioning — prevents production build race
      const t = setTimeout(() => onDoneRef.current(), 400);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count]);

  const circumference = 2 * Math.PI * 52;
  const dashOffset = circumference * (1 - count / 3);

  return (
    <div style={{ minHeight: "100vh", background: C.navyDeep, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 28 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "radial-gradient(ellipse 50% 40% at 50% 40%, #0d2e4a 0%, transparent 70%)", pointerEvents: "none" }} />

      <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 24 }}>
        <div style={{ fontFamily: S.font.serif, fontSize: 15, color: "rgba(255,255,255,0.22)" }}>
          Teevra <em style={{ fontStyle: "italic", color: "rgba(35,128,191,0.45)" }}>Diagnostic</em>
        </div>

        <div style={{ fontFamily: S.font.sans, fontSize: 13, fontWeight: 500, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)" }}>
          Test begins in
        </div>

        {/* Countdown ring */}
        <svg width="140" height="140" viewBox="0 0 140 140">
          <circle cx="70" cy="70" r="52" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
          <circle cx="70" cy="70" r="52" fill="none" stroke={C.blue} strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transform: "rotate(-90deg)", transformOrigin: "70px 70px", transition: "stroke-dashoffset 0.9s linear" }}
          />
          <text x="70" y="86" textAnchor="middle"
            fontFamily="DM Serif Display, serif" fontSize="56"
            fill={C.cream}
            style={{ transition: "opacity 0.15s" }}>
            {count === 0 ? "Go" : count}
          </text>
        </svg>

        <div style={{ fontFamily: S.font.sans, fontSize: 13, color: "rgba(255,255,255,0.28)", textAlign: "center", lineHeight: 1.7, maxWidth: 320 }}>
          Press <strong style={{ color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>Space</strong> or <strong style={{ color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>tap</strong> for every shape — except the circle
        </div>
      </div>
    </div>
  );
}

// ─── Error boundary — catches crashes inside ScreenTest and shows a safe screen
class TestErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error: error.message || "Unknown error" };
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", background: C.navyDeep, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20, padding: 40 }}>
          <div style={{ fontFamily: S.font.serif, fontSize: 28, color: C.cream, textAlign: "center" }}>Something went wrong</div>
          <div style={{ fontFamily: S.font.sans, fontSize: 13, color: "rgba(255,255,255,0.4)", textAlign: "center", maxWidth: 380, lineHeight: 1.7 }}>
            An unexpected error occurred during the test. Please close this window, reopen it, and try again. If the problem persists, contact your administrator.
          </div>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.05)", padding: "8px 14px", borderRadius: 6 }}>
            {this.state.error}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Screen 6: Main Test ─────────────────────────────────────────────────────
const HALFTIME = 180; // break after this many trials

function ScreenBreak({ onResume, elapsed }) {
  const [countdown, setCountdown] = useState(60);
  useEffect(() => {
    const iv = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(iv); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const circumference = 2 * Math.PI * 28;
  const dashOffset = circumference * (1 - countdown / 60);

  return (
    <div style={{ minHeight: "100vh", background: C.navyDeep, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 32, padding: 40 }}>
      {/* Radial glow */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "radial-gradient(ellipse 50% 40% at 50% 40%, #0d2e4a 0%, transparent 70%)", pointerEvents: "none" }} />

      <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 28 }}>
        {/* Logo */}
        <div style={{ fontFamily: S.font.serif, fontSize: 15, color: "rgba(255,255,255,0.22)" }}>
          Teevra <em style={{ fontStyle: "italic", color: "rgba(35,128,191,0.45)" }}>Diagnostic</em>
        </div>

        {/* Break card */}
        <div style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "40px 52px", display: "flex", flexDirection: "column", alignItems: "center", gap: 20, maxWidth: 420 }}>

          {/* Countdown ring */}
          <svg width="72" height="72" viewBox="0 0 72 72">
            <circle cx="36" cy="36" r="28" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="4" />
            <circle cx="36" cy="36" r="28" fill="none" stroke={C.blue} strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              style={{ transform: "rotate(-90deg)", transformOrigin: "36px 36px", transition: "stroke-dashoffset 1s linear" }}
            />
            <text x="36" y="41" textAnchor="middle" fontFamily="DM Serif Display,serif" fontSize="18" fill={C.cream}>{countdown}</text>
          </svg>

          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: S.font.serif, fontSize: 28, color: C.cream, marginBottom: 8 }}>Halfway there</div>
            <div style={{ fontFamily: S.font.sans, fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.7 }}>
              You've completed <strong style={{ color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>180 of 360 trials</strong>.<br />
              Take a short break. The second half begins automatically in {countdown} seconds, or press the button when you're ready.
            </div>
          </div>

          {/* Stats row */}
          <div style={{ display: "flex", gap: 20, borderTop: "0.5px solid rgba(255,255,255,0.07)", paddingTop: 20, width: "100%" }}>
            {[["Elapsed", `${mm}:${ss}`], ["Trials done", "180"], ["Remaining", "180"]].map(([l, v]) => (
              <div key={l} style={{ flex: 1, textAlign: "center" }}>
                <div style={{ fontFamily: S.font.serif, fontSize: 20, color: C.blueLight }}>{v}</div>
                <div style={{ fontFamily: S.font.sans, fontSize: 10, fontWeight: 500, letterSpacing: "0.09em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", marginTop: 2 }}>{l}</div>
              </div>
            ))}
          </div>

          <button onClick={onResume} style={{ background: C.blue, color: "#fff", border: "none", borderRadius: 100, padding: "13px 36px", fontFamily: S.font.sans, fontSize: 14, fontWeight: 500, cursor: "pointer", width: "100%" }}>
            Continue test →
          </button>
        </div>

        <div style={{ fontFamily: S.font.sans, fontSize: 11, color: "rgba(255,255,255,0.18)", textAlign: "center" }}>
          Do not close this window. Your progress is saved.
        </div>
      </div>
    </div>
  );
}

function ScreenTest({ onDone }) {
  const trials = useRef(generateTrials());
  const results = useRef([]);
  const [idx, setIdx] = useState(0);
  const [showing, setShowing] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [onBreak, setOnBreak] = useState(false);
  const [showingCountdown, setShowingCountdown] = useState(false);
  const startTimeRef = useRef(null);
  const trialStartRef = useRef(null);
  const respondedRef = useRef(false);
  const timerRef = useRef(null);
  const breakShownRef = useRef(false);
  const onBreakRef = useRef(false);
  const trialRef = useRef(null);       // always holds current trial for timer callbacks
  const onDoneRef = useRef(onDone);    // always holds latest onDone without causing re-runs

  // Keep refs in sync
  useEffect(() => { onBreakRef.current = onBreak; }, [onBreak]);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  const total = trials.current.length;
  const trial = trials.current[Math.min(idx, total - 1)];
  trialRef.current = trial; // sync ref every render — safe, synchronous
  const block = trial ? trial.block : 6;
  const done = idx >= total;

  const handleResponse = useCallback(() => {
    if (respondedRef.current || onBreakRef.current) return;
    respondedRef.current = true;
    const rt = trialStartRef.current ? Math.round(performance.now() - trialStartRef.current) : 0;
    results.current[idx] = { ...results.current[idx], responded: true, rt };
  }, [idx]);

  useEffect(() => {
    const handler = (e) => { if (e.code === "Space") { e.preventDefault(); handleResponse(); } };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleResponse]);

  // Elapsed timer — keeps running through break
  useEffect(() => {
    startTimeRef.current = performance.now();
    const iv = setInterval(() => setElapsed(Math.floor((performance.now() - startTimeRef.current) / 1000)), 1000);
    return () => clearInterval(iv);
  }, []);

  // Trial runner — reads trial and onDone via refs to keep dependency array honest
  useEffect(() => {
    if (done) {
      // Ensure the last trial slot is never a sparse hole before calling onDone.
      // If the timer fired before initialisation completed, write a missed-trial
      // record so trial 360 always exists in the results array.
      const lastIdx = trials.current.length - 1;
      if (lastIdx >= 0 && !results.current[lastIdx]) {
        const lastTrial = trials.current[lastIdx];
        if (lastTrial) {
          results.current[lastIdx] = { ...lastTrial, responded: false, rt: 0 };
        }
      }
      onDoneRef.current(results.current);
      return;
    }

    if (idx === HALFTIME && !breakShownRef.current) {
      breakShownRef.current = true;
      clearTimeout(timerRef.current);
      setOnBreak(true);
      return;
    }

    const t = trialRef.current;
    setShowing(true);
    respondedRef.current = false;
    trialStartRef.current = performance.now();
    results.current[idx] = { ...t, responded: false, rt: 0 };

    timerRef.current = setTimeout(() => {
      setShowing(false);
      timerRef.current = setTimeout(() => {
        setIdx((i) => i + 1);
      }, t.actualIsi - STIMULUS_DURATION);
    }, STIMULUS_DURATION);

    return () => clearTimeout(timerRef.current);
  }, [idx, done]);

  // Auto-resume after 60s
  useEffect(() => {
    if (!onBreak) return;
    const t = setTimeout(() => {
      setOnBreak(false);
      setShowingCountdown(true); // brief countdown before second half
    }, 60000);
    return () => clearTimeout(t);
  }, [onBreak]);

  const handleResume = useCallback((e) => {
    if (e) e.stopPropagation();
    setOnBreak(false);
    setShowingCountdown(true); // brief countdown before second half
  }, []);

  const handleCountdownDone = useCallback(() => {
    setShowingCountdown(false);
    setIdx((i) => i + 1); // advance to trial 181
  }, []);

  if (onBreak) return <ScreenBreak onResume={handleResume} elapsed={elapsed} />;
  if (showingCountdown) return <ScreenCountdown onDone={handleCountdownDone} />;

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const progress = total > 0 ? (idx / total) * 100 : 0;

  return (
    <div style={{ minHeight: "100vh", background: C.navyDeep, display: "flex", flexDirection: "column" }} onClick={handleResponse}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 28px", borderBottom: "0.5px solid rgba(255,255,255,0.05)" }}>
        <div style={{ fontFamily: S.font.serif, fontSize: 15, color: "rgba(255,255,255,0.22)" }}>Teevra <em style={{ fontStyle: "italic", color: "rgba(35,128,191,0.5)" }}>Diagnostic</em></div>
        <div style={{ display: "flex", gap: 22, alignItems: "center" }}>
          {[["Trial", Math.min(idx + 1, total)], ["Total", total], ["Elapsed", `${mm}:${ss}`], ["Block", `${block} of 6`]].map(([l, v]) => (
            <div key={l} style={{ textAlign: "center" }}>
              <span style={{ fontFamily: S.font.serif, fontSize: 18, color: C.cream, display: "block" }}>{v}</span>
              <span style={{ fontFamily: S.font.sans, fontSize: 9, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", display: "block", marginTop: 1 }}>{l}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ height: 2, background: "rgba(255,255,255,0.05)" }}><div style={{ height: "100%", width: `${progress}%`, background: C.blue, transition: "width 0.3s" }} /></div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 44 }}>
        <div style={{ width: 220, height: 220, background: "#0D1C2E", borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", border: `0.5px solid rgba(35,128,191,0.12)` }}>
          {showing && trial && <ShapeSVG shape={trial.shape} color={C.blueLight} />}
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} style={{ width: 28, height: 4, borderRadius: 2, background: i + 1 < block ? C.blue : i + 1 === block ? C.blueLight : "rgba(255,255,255,0.09)" }} />
            ))}
            <span style={{ fontFamily: S.font.sans, fontSize: 10, color: "rgba(255,255,255,0.22)", marginLeft: 4 }}>Block {block} of 6</span>
          </div>
          <div style={{ fontFamily: S.font.sans, fontSize: 12, color: "rgba(255,255,255,0.18)", textAlign: "center" }}>
            Press <strong style={{ color: "rgba(255,255,255,0.38)" }}>Space</strong> or <strong style={{ color: "rgba(255,255,255,0.38)" }}>tap</strong> for every shape — except the circle
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Screen 7: Saving ────────────────────────────────────────────────────────
function ScreenSaving() {
  return (
    <div style={{ minHeight: "100vh", background: C.navyDeep, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
      <div style={{ width: 48, height: 48, border: `3px solid rgba(35,128,191,0.3)`, borderTop: `3px solid ${C.blue}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <div style={{ fontFamily: S.font.sans, fontSize: 15, color: "rgba(255,255,255,0.5)" }}>Saving your results…</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Screen 8: Report ────────────────────────────────────────────────────────
function ScreenReport({ scores, participant, sessionId }) {
  const { firstName, lastName, grade, school, board, city, sex } = participant;
  const name = `${firstName} ${lastName}`;
  const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  // ── Invalid session screen ────────────────────────────────────────────────────
  if (!scores.isValidSession) {
    return (
      <div style={{ background: C.sand, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ background: C.navy, padding: "22px 36px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontFamily: S.font.serif, fontSize: 18, color: C.cream }}>
              Teevra <em style={{ fontStyle: "italic", color: C.blueLight }}>Diagnostic</em> — Assessment Report
            </div>
            <div style={{ fontFamily: S.font.sans, fontSize: 12, color: "rgba(255,255,255,0.38)", marginTop: 4 }}>
              <strong style={{ color: "rgba(255,255,255,0.65)", fontWeight: 500 }}>{name}</strong> · {grade} · {school} ({board}) · {city} · {today}
            </div>
          </div>
        </div>
        {/* Invalid body */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 36px", gap: 28, maxWidth: 600, margin: "0 auto" }}>
          {/* Icon */}
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#FAEEDA", border: "0.5px solid #EF9F27", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>
            ⚠
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: S.font.serif, fontSize: 28, color: C.text, marginBottom: 10 }}>
              This session could not be scored
            </div>
            <div style={{ fontFamily: S.font.sans, fontSize: 14, color: C.muted, lineHeight: 1.75, marginBottom: 28 }}>
              The Attention Score requires a minimum level of engagement to produce a meaningful result.
              This session did not meet one or more of the required thresholds.
            </div>
          </div>
          {/* Failure reasons */}
          <div style={{ background: "white", border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "20px 24px", width: "100%" }}>
            <div style={{ fontFamily: S.font.sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: C.muted, marginBottom: 14 }}>
              Reasons
            </div>
            {scores.validationFailures.map((reason, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: i < scores.validationFailures.length - 1 ? 10 : 0 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#BA7517", marginTop: 6, flexShrink: 0 }} />
                <div style={{ fontFamily: S.font.sans, fontSize: 13, color: C.textDark, lineHeight: 1.6 }}>{reason}</div>
              </div>
            ))}
            <div style={{ borderTop: `0.5px solid ${C.borderLight}`, marginTop: 16, paddingTop: 14, fontFamily: S.font.sans, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              Overall response rate: <strong style={{ color: C.textDark, fontWeight: 500 }}>{scores.responseRate}%</strong> ({Math.round(scores.responseRate * 3.6)} of 360 trials received a response)
            </div>
          </div>
          {/* What to do */}
          <div style={{ background: "#EBF4FB", border: `0.5px solid #7ABFE0`, borderRadius: 12, padding: "18px 22px", width: "100%" }}>
            <div style={{ fontFamily: S.font.sans, fontSize: 13, color: "#1a4a7a", lineHeight: 1.7 }}>
              <strong style={{ fontWeight: 500 }}>What to do next:</strong> Raw session data has been saved securely. Please re-administer the test under supervised conditions, ensuring the participant understands the instructions and is ready to engage for the full duration.
            </div>
          </div>
          <div style={{ fontFamily: S.font.sans, fontSize: 11, color: C.muted, textAlign: "center", lineHeight: 1.65 }}>
            Session ID: {sessionId} · This record has been saved. To request deletion contact your administrator.
          </div>
        </div>
      </div>
    );
  }

  // Pronoun set based on captured sex field
  const pr = (() => {
    if (sex === "Female") return { sub: "she", obj: "her", pos: "her", cap: "She", capObj: "Her" };
    if (sex === "Male")   return { sub: "he",  obj: "him", pos: "his", cap: "He",  capObj: "His" };
    return                       { sub: "they", obj: "them", pos: "their", cap: "They", capObj: "Their" };
  })();

  // ── Test summary — four structured paragraphs, parent-friendly language ──
  const msToWords = (ms) => {
    if (ms < 350) return `${ms}ms — under a third of a second`;
    if (ms < 500) return `${ms}ms — roughly half a second`;
    if (ms < 750) return `${ms}ms — about three quarters of a second`;
    if (ms < 1200) return `${ms}ms — just over a second`;
    if (ms < 2000) return `${ms}ms — about one and a half seconds`;
    return `${ms}ms — over two seconds`;
  };

  const sdToWords = (sd, mean) => {
    if (!mean || mean === 0) return `a standard deviation of ${sd}ms — not enough valid responses to measure consistency`;
    const ratio = sd / mean;
    if (ratio < 0.2) return `a standard deviation of ${sd}ms — ${firstName}'s response speed was quite consistent throughout`;
    if (ratio < 0.35) return `a standard deviation of ${sd}ms — ${firstName}'s speed fluctuated by roughly ${Math.round(ratio * 100)}% of ${pr.pos} mean on a trial-to-trial basis`;
    return `a standard deviation of ${sd}ms — ${firstName}'s speed varied considerably from one trial to the next`;
  };

  const para1 = (() => {
    const hitRate = scores.hitRate.toFixed(1);
    const inhibitRate = (100 - scores.commissionPct).toFixed(1);
    return `${firstName} completed the full test — all 360 shapes across 6 rounds. ${pr.cap} correctly pressed for ${hitRate}% of the shapes ${pr.sub} was supposed to respond to, and held back on the circle (which ${pr.sub} wasn't supposed to press) ${inhibitRate}% of the time. ${pr.capObj} average time to press after a shape appeared was ${msToWords(scores.meanRT)}.`;
  })();

  const para2 = (() => {
    const decrement = scores.vigilanceDecrement;
    const slope = scores.blockStats;
    const earlyAcc = slope.length >= 2 ? Math.round((slope[0].accuracy + slope[1].accuracy) / 2) : 0;
    const lateAcc = slope.length >= 6 ? Math.round((slope[4].accuracy + slope[5].accuracy) / 2) : 0;
    if (decrement <= 0.05) {
      return `${firstName}'s accuracy and response speed stayed steady from the first round to the last. ${pr.cap} maintained ${pr.pos} focus consistently throughout the full duration of the test — there was no meaningful drop in performance as the session went on.`;
    } else if (decrement <= 0.12) {
      return `${firstName} started the test well — ${pr.pos} accuracy in the first two rounds was around ${earlyAcc}%. By the final rounds this had eased to around ${lateAcc}%, and ${pr.pos} responses slowed slightly. This kind of gradual shift over a 15-minute task is common, and tells us something about how ${pr.pos} attention holds up over a sustained period.`;
    } else {
      return `${firstName}'s accuracy dropped noticeably as the test progressed — from around ${earlyAcc}% in the early rounds to around ${lateAcc}% by the end. ${pr.capObj} response times also slowed across blocks. This pattern suggests the task became more effortful for ${pr.obj} as it continued, and that sustaining focus over the full session was challenging.`;
    }
  })();

  const para3 = (() => {
    // Branch on overall commission/omission rates as proxy for ISI sensitivity
    const commHigh = scores.commissionPct > 8;
    const omHigh   = scores.omissionPct > 12;
    if (!commHigh && !omHigh) {
      return `The test varied how long ${firstName} had to wait between shapes — sometimes just 1 second, sometimes up to 4 seconds. ${pr.cap} responded consistently across all three gap lengths, which suggests ${pr.sub} was able to stay ready and alert regardless of how long ${pr.sub} had to wait.`;
    } else if (commHigh) {
      return `The test deliberately varied how long ${firstName} had to wait between shapes — 1 second, 2 seconds, or 4 seconds. When the gaps were longer and more unpredictable, ${pr.sub} pressed for the circle slightly more often than ${pr.sub} did in faster-paced sections. Staying alert and holding back a response during longer, uncertain gaps took more effort.`;
    } else {
      return `The test deliberately varied the gap between shapes — 1 second, 2 seconds, or 4 seconds. During longer gaps, ${firstName} occasionally missed a shape ${pr.sub} should have pressed for. Maintaining readiness across a 4-second wait is more demanding than staying alert during shorter, quicker gaps.`;
    }
  })();

  const para4 = (() => {
    const p = scores.perseverations;
    const sdLine = sdToWords(scores.rtStdDev, scores.meanRT);
    const persLine = p === 0
      ? `${firstName} had no perseverations — every response ${pr.sub} gave was within a reaction time that reflects genuine detection.`
      : `${firstName} also had ${p} response${p === 1 ? "" : "s"} that arrived in under 100ms — too fast to be a genuine reaction to what appeared on screen. ${p <= 3 ? "This is a very small number across 360 trials, likely accidental presses." : "These likely represent anticipatory or accidental key presses rather than deliberate responses."}`;
    return `Looking at speed and consistency: ${firstName} had ${sdLine}. ${persLine}`;
  })();

  const summaryParas = [para1, para2, para3, para4];

  const svgW = 260;
  const chartH = 100;
  const maxRT = Math.max(...scores.blockStats.map((b) => b.meanRT));

  return (
    <div style={{ background: C.sand, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ background: C.navy, padding: "22px 36px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontFamily: S.font.serif, fontSize: 18, color: C.cream }}>Teevra <em style={{ fontStyle: "italic", color: C.blueLight }}>Diagnostic</em> — Assessment Report</div>
          <div style={{ fontFamily: S.font.sans, fontSize: 12, color: "rgba(255,255,255,0.38)", marginTop: 4 }}>
            <strong style={{ color: "rgba(255,255,255,0.65)", fontWeight: 500 }}>{name}</strong> · {grade} · {school} ({board}) · {city} · {today}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => window.print()} className="print-btn" style={{ background: C.blue, border: "none", borderRadius: 100, padding: "9px 18px", fontFamily: S.font.sans, fontSize: 12, fontWeight: 500, color: "#fff", cursor: "pointer" }}>⎙ Print report</button>
          <style>{`@media (max-width: 768px) { .print-btn { display: none; } }`}</style>
        </div>
      </div>

      <div style={{ padding: "28px 36px", display: "flex", flexDirection: "column", gap: 20, maxWidth: 1100 }}>

        {/* Attention Score */}
        <div style={{ background: C.navy, borderRadius: 14, padding: "24px 28px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 32 }}>

            {/* Score */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 100 }}>
              <div style={{ fontFamily: S.font.sans, fontSize: 10, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.32)", marginBottom: 8 }}>Attention Score</div>
              <div style={{ fontFamily: S.font.serif, fontSize: 64, color: C.blueLight, lineHeight: 1 }}>{scores.tas}</div>
              <div style={{ fontFamily: S.font.sans, fontSize: 12, color: "rgba(255,255,255,0.3)", marginTop: 4 }}>out of 100</div>
            </div>

            {/* Divider */}
            <div style={{ width: 1, background: "rgba(255,255,255,0.07)", alignSelf: "stretch" }} />

            {/* Four components */}
            <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { label: "Detection accuracy",   val: scores.tasDetection,   desc: "How consistently targets were identified" },
                { label: "Inhibitory control",   val: scores.tasInhibition,  desc: "How well circle responses were suppressed" },
                { label: "Response consistency", val: scores.tasConsistency, desc: "Stability of response speed across trials" },
                { label: "Sustained attention",  val: scores.tasVigilance,   desc: "How well performance held across all 6 blocks" },
              ].map(({ label, val, desc }) => (
                <div key={label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "12px 14px", border: "0.5px solid rgba(255,255,255,0.07)" }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
                    <div style={{ fontFamily: S.font.sans, fontSize: 10, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)" }}>{label}</div>
                    <div style={{ fontFamily: S.font.serif, fontSize: 18, color: C.blueLight }}>{val}<span style={{ fontFamily: S.font.sans, fontSize: 10, color: "rgba(255,255,255,0.3)", marginLeft: 2 }}>/25</span></div>
                  </div>
                  {/* Component bar */}
                  <div style={{ height: 3, background: "rgba(255,255,255,0.07)", borderRadius: 2, marginBottom: 6 }}>
                    <div style={{ width: `${(val / 25) * 100}%`, height: "100%", borderRadius: 2, background: C.blue }} />
                  </div>
                  <div style={{ fontFamily: S.font.sans, fontSize: 11, color: "rgba(255,255,255,0.28)", lineHeight: 1.4 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Caveat */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "0.5px solid rgba(255,255,255,0.07)", fontFamily: S.font.sans, fontSize: 11, color: "rgba(255,255,255,0.28)", lineHeight: 1.65 }}>
            The Attention Score is designed for longitudinal comparison within the same participant over time. It should not be used to compare scores between different individuals. Equal weight is given to each of the four components.
          </div>
        </div>

        {/* Abandonment flag — shown only when hard stop detected */}
        {scores.isAbandoned && (
          <div style={{ background: "#FAEEDA", border: `0.5px solid #EF9F27`, borderRadius: 12, padding: "14px 18px", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 16, marginTop: 1 }}>⚠</span>
            <div style={{ fontFamily: S.font.sans, fontSize: 13, color: "#5C3A00", lineHeight: 1.65 }}>
              <strong style={{ fontWeight: 500 }}>Performance stopped abruptly during the test.</strong> Three or more consecutive blocks had near-zero accuracy, suggesting the participant stopped engaging before the test ended. The Attention Score reflects this pattern. The Test Summary describes gradual attention changes — please note this flag when interpreting the vigilance section.
            </div>
          </div>
        )}

        {/* Metric cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {[
            { label: "Omissions", val: scores.omissionPct, unit: "%", sub: "Missed targets" },
            { label: "Commissions", val: scores.commissionPct, unit: "%", sub: "False alarms on circle" },
            { label: "Mean hit RT", val: scores.meanRT, unit: "ms", sub: "Avg. response time" },
            { label: "RT std dev", val: scores.rtStdDev, unit: "ms", sub: "Response consistency" },
          ].map(({ label, val, unit, sub }) => (
            <div key={label} style={{ background: "white", border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ fontFamily: S.font.sans, fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: C.muted, marginBottom: 5 }}>{label}</div>
              <div style={{ fontFamily: S.font.serif, fontSize: 26, color: C.text, lineHeight: 1 }}>{val}<span style={{ fontSize: 14, color: C.muted }}>{unit}</span></div>
              <div style={{ fontFamily: S.font.sans, fontSize: 11, color: C.muted, display: "block" }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {[
            { title: "Vigilance curve — accuracy across blocks", data: scores.blockStats.map((b) => b.accuracy), max: 100, color: C.blue, unit: "%" },
            { title: "Response time drift across blocks", data: scores.blockStats.map((b) => b.meanRT), max: Math.max(maxRT + 100, 200), color: "#BA7517", unit: "ms" },
          ].map(({ title, data, max, color, unit }) => {
            const pts = data.map((v, i) => {
              const x = 20 + i * (svgW - 40) / 5;
              const y = chartH - 10 - ((v / max) * (chartH - 20));
              return `${x},${y}`;
            }).join(" ");
            return (
              <div key={title} style={{ background: "white", border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "16px 18px" }}>
                <div style={{ fontFamily: S.font.sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted, marginBottom: 12 }}>{title}</div>
                <svg width="100%" height={chartH} viewBox={`0 0 ${svgW} ${chartH}`}>
                  {[0.25, 0.5, 0.75].map((f) => <line key={f} x1="0" y1={chartH - 10 - f * (chartH - 20)} x2={svgW} y2={chartH - 10 - f * (chartH - 20)} stroke="#E8E6DF" strokeWidth="0.5" />)}
                  <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                  {data.map((v, i) => {
                    const x = 20 + i * (svgW - 40) / 5;
                    const y = chartH - 10 - ((v / max) * (chartH - 20));
                    return <circle key={i} cx={x} cy={y} r="3" fill={color} />;
                  })}
                  {[1, 2, 3, 4, 5, 6].map((b, i) => {
                    const x = 20 + i * (svgW - 40) / 5;
                    return <text key={b} x={x} y={chartH} fontFamily="DM Sans,sans-serif" fontSize="9" fill="#888780" textAnchor="middle">B{b}</text>;
                  })}
                </svg>
              </div>
            );
          })}
        </div>

        {/* Test summary */}
        <div style={{ background: "#EBF4FB", borderRadius: 12, padding: "24px 28px", borderLeft: `3px solid ${C.blue}` }}>
          <div style={{ fontFamily: S.font.serif, fontSize: 20, color: C.navy, marginBottom: 20 }}>Test summary</div>
          {[
            { label: "How " + firstName + " did overall",                para: summaryParas[0] },
            { label: "How attention held up over time",                   para: summaryParas[1] },
            { label: "Short gaps vs long gaps between shapes",            para: summaryParas[2] },
            { label: "Speed and consistency",                             para: summaryParas[3] },
          ].map(({ label, para }, i, arr) => (
            <div key={label} style={{ marginBottom: i < arr.length - 1 ? 18 : 0, paddingBottom: i < arr.length - 1 ? 18 : 0, borderBottom: i < arr.length - 1 ? "0.5px solid rgba(35,128,191,0.15)" : "none" }}>
              <div style={{ fontFamily: S.font.sans, fontSize: 10, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase", color: C.blue, marginBottom: 6 }}>{label}</div>
              <div style={{ fontFamily: S.font.sans, fontSize: 13, color: "#1a3a5c", lineHeight: 1.85 }}>{para}</div>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div style={{ fontFamily: S.font.sans, fontSize: 11, color: C.muted, lineHeight: 1.65, borderTop: `0.5px solid ${C.border}`, paddingTop: 16 }}>
          This report is generated by Teevra Diagnostic, a non-clinical computerised attention screening tool. All scores are direct measurements from this session and do not constitute a clinical diagnosis. The Attention Score is intended for longitudinal tracking within the same participant and should not be used to compare scores between individuals. Results should be interpreted by a qualified educational or clinical professional. Session ID: {sessionId}. To request data deletion, contact your administrator.
        </div>
      </div>
    </div>
  );
}

// ─── App Root ────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("intro");
  const [participant, setParticipant] = useState(null);
  const [scores, setScores] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [consentData, setConsentData] = useState(null);
  const sessionIdRef = useRef(null); // ref ensures handleTestDone always reads the latest id

  const handleDataCapture = async (data) => {
    setParticipant(data);
    // consentData is set by ScreenConsent — guard against null in case
    // of unexpected navigation that bypasses the consent screen
    const safeConsent = consentData || { guardianName: "", guardianRelation: "" };
    const id = await apiStartSession({
      participant: {
        firstName: data.firstName, lastName: data.lastName,
        dob: data.dob,            age: data.age,
        sex: data.sex,            grade: data.grade,
        city: data.city,          email: data.email,
      },
      school:        { name: data.school, board: data.board },
      screening:     { screenTime: data.screenTime, screenActivity: data.screenActivity },
      administrator: { name: null, role: null, notes: null },
      consent:       safeConsent,
    });
    sessionIdRef.current = id;
    setSessionId(id);
  };

  const handleTestDone = async (trialResults) => {
    setScreen("saving");
    // Fill any sparse holes in the results array (e.g. trial 360 if timer raced onDone)
    // then filter out any genuinely null slots and reindex from 1
    const filledTrials = Array.from(
      { length: trialResults.length },
      (_, i) => trialResults[i] || null
    );
    const validTrials = filledTrials.filter(Boolean);
    const s = computeScores(validTrials);
    const apiTrials = validTrials.map((t, i) => ({
      trialNumber: i + 1,
      block:       t.block,
      isi:         t.isi,
      actualIsi:   t.actualIsi,
      shape:       t.shape,
      isTarget:    t.isTarget,
      responded:   t.responded,
      rt:          t.rt,
      correct:     t.isTarget ? t.responded : !t.responded,
    }));
    await apiCompleteSession(sessionIdRef.current, {
      tas:                s.tas,
      tasDetection:       s.tasDetection,
      tasInhibition:      s.tasInhibition,
      tasConsistency:     s.tasConsistency,
      tasVigilance:       s.tasVigilance,
      percentile:         s.percentile,
      omissionPct:        s.omissionPct,
      commissionPct:      s.commissionPct,
      hitRate:            s.hitRate,
      meanRT:             s.meanRT,
      rtStdDev:           s.rtStdDev,
      perseverations:     s.perseverations,
      vigilanceDecrement: s.vigilanceDecrement,
      vigilanceSlope:     s.vigilanceSlope,
    }, apiTrials);
    setScores(s);
    setScreen("report");
  };

  if (screen === "intro") return <ScreenIntro onNext={() => setScreen("consent")} />;
  if (screen === "consent") return <ScreenConsent onNext={() => setScreen("data")} onBack={() => setScreen("intro")} onConsent={setConsentData} />;
  if (screen === "data") return <ScreenDataCapture onNext={() => setScreen("instructions")} onBack={() => setScreen("consent")} onData={handleDataCapture} />;
  if (screen === "instructions") return <ScreenInstructions onNext={() => setScreen("practice")} onBack={() => setScreen("data")} />;
  if (screen === "practice") return <ScreenPractice onNext={() => setScreen("countdown")} onBack={() => setScreen("instructions")} />;
  if (screen === "countdown") return <ScreenCountdown onDone={() => setScreen("test")} />;
  if (screen === "test") return <TestErrorBoundary><ScreenTest onDone={handleTestDone} /></TestErrorBoundary>;
  if (screen === "saving") return <ScreenSaving />;
  if (screen === "report") return <ScreenReport scores={scores} participant={participant} sessionId={sessionId} />;
  return null;
}
